import React, { useEffect, useState } from "react";
import { HashRouter, Routes, Route, NavLink, useLocation, useNavigate } from "react-router-dom";
import { Sun, Moon } from "lucide-react";
import { CF_STYLE, stileSfondoApp } from "./style";
import { AppProvider, useApp } from "./AppContext";
import { AuthProvider, useAuth } from "./AuthContext";
import { getTracciato } from "./api";
import { MODULI, moduloDaPercorso } from "./moduli";
import { VERSIONE, DATA_BUILD, COMMIT } from "./versione";
import SchermataLogin from "./SchermataLogin";
import PannelloImpostazioni from "./PannelloImpostazioni";
import Scelta from "./pages/Scelta";
import Home from "./pages/Home";
import Mapping from "./pages/Mapping";
import Revisione from "./pages/Revisione";
import OrdiniMapping from "./pages/OrdiniMapping";
import CalibrazionePdf from "./pages/CalibrazionePdf";
import ConversioneCodici from "./pages/ConversioneCodici";

function etichettaSessione(pathname) {
  if (pathname.startsWith("/ordini")) return "IMPORT ORDINI";
  if (pathname.startsWith(MODULI.ddt.percorsoBase)) return MODULI.ddt.etichettaSessione;
  if (pathname.startsWith(MODULI.diba.percorsoBase)) return MODULI.diba.etichettaSessione;
  return null;
}

function vociNavPerPercorso(pathname, puoMappare) {
  const voci = [{ path: "/", etichetta: "HOME" }];
  if (pathname.startsWith("/ordini")) {
    // Un'unica pagina vera (la mappatura nativa, in "/ordini" stesso): non
    // c'è più uno strumento esterno da scegliere prima, quindi niente tab
    // multiple da mostrare qui.
  } else if (pathname.startsWith(MODULI.ddt.percorsoBase) || pathname.startsWith(MODULI.diba.percorsoBase)) {
    const base = pathname.startsWith(MODULI.ddt.percorsoBase) ? MODULI.ddt.percorsoBase : MODULI.diba.percorsoBase;
    voci.push({ path: base, etichetta: "CARICA FILE" });
    // Chi non può mappare non deve vedere nemmeno la tab: la pagina Mapping
    // la rimanda comunque indietro da sola (vedi Mapping.jsx), ma qui non
    // deve nemmeno comparire come opzione cliccabile.
    if (puoMappare) voci.push({ path: `${base}/mapping`, etichetta: "MAPPING" });
    voci.push({ path: `${base}/revisione`, etichetta: "REVISIONE" });
  }
  return voci;
}

function Intestazione({ temaScuro, setTemaScuro, onApriImpostazioni }) {
  const location = useLocation();
  const { username, logout, puoMappare, azienda } = useAuth();
  const [menuAperto, setMenuAperto] = useState(false);
  const vociNav = vociNavPerPercorso(location.pathname, puoMappare);
  const sessione = etichettaSessione(location.pathname);

  return (
    <header
      style={{
        background: "linear-gradient(180deg, #334155 0%, #475569 100%)",
        borderRadius: "8px",
        color: "#e2e8f0",
        marginBottom: "30px",
        boxShadow: "0 4px 20px -4px rgba(0,0,0,0.4)",
        border: "1px solid #475569",
      }}
    >
      <div
        style={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          padding: "9px 24px",
          backgroundColor: "#0f172a",
          borderBottom: "1px solid #475569",
          borderTopLeftRadius: "8px",
          borderTopRightRadius: "8px",
          fontFamily: "'Roboto Mono', Consolas, monospace",
          fontSize: "13px",
          letterSpacing: "0.6px",
          color: "#cbd5e1",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
          <div style={{ position: "relative" }}>
            <button
              className="cf-btn"
              onClick={() => setMenuAperto((v) => !v)}
              title="Menu"
              style={{
                padding: "5px 10px", borderRadius: "4px", border: "1px solid #475569",
                backgroundColor: menuAperto ? "#1e293b" : "transparent", color: "#cbd5e1",
                cursor: "pointer", fontSize: "15px", lineHeight: 1,
              }}
            >
              ☰
            </button>
            {menuAperto && (
              <>
                <div onClick={() => setMenuAperto(false)} style={{ position: "fixed", inset: 0, zIndex: 998 }} />
                <div style={{
                  position: "absolute", top: "34px", left: 0, zIndex: 999,
                  backgroundColor: "#1e293b", border: "1px solid #475569", borderRadius: "8px",
                  boxShadow: "0 10px 30px rgba(0,0,0,0.45)", minWidth: "190px", overflow: "hidden",
                }}>
                  {/* Il pannello Impostazioni è comunque protetto da una password fissa
                      (vedi PannelloImpostazioni.jsx), ma solo l'utente admin "wtrendy" deve
                      vederne pure il collegamento: gli altri operatori non ci accedono. */}
                  {username === "wtrendy" && (
                    <>
                      <button
                        className="cf-btn"
                        onClick={() => { setMenuAperto(false); onApriImpostazioni(); }}
                        style={{
                          display: "block", width: "100%", textAlign: "left", padding: "11px 16px",
                          border: "none", backgroundColor: "transparent", color: "#f1f5f9", cursor: "pointer",
                          fontFamily: "'Roboto Mono', Consolas, monospace", fontSize: "13px", letterSpacing: "0.3px",
                        }}
                      >
                        ⚙️&nbsp;&nbsp;Impostazioni
                      </button>
                      <div style={{ height: "1px", backgroundColor: "#475569" }} />
                    </>
                  )}
                  <button
                    className="cf-btn"
                    onClick={() => { setTemaScuro((v) => !v); setMenuAperto(false); }}
                    title="Preferenza personale, salvata solo su questo browser"
                    style={{
                      display: "flex", alignItems: "center", gap: "10px", width: "100%", textAlign: "left", padding: "11px 16px",
                      border: "none", backgroundColor: "transparent", color: "#f1f5f9", cursor: "pointer",
                      fontFamily: "'Roboto Mono', Consolas, monospace", fontSize: "13px", letterSpacing: "0.3px",
                    }}
                  >
                    {temaScuro ? <Sun size={15} strokeWidth={2} /> : <Moon size={15} strokeWidth={2} />}{temaScuro ? "Light Mode" : "Dark Mode"}
                  </button>
                  <div style={{ height: "1px", backgroundColor: "#475569" }} />
                  <button
                    className="cf-btn"
                    onClick={() => { setMenuAperto(false); logout(); }}
                    style={{
                      display: "block", width: "100%", textAlign: "left", padding: "11px 16px",
                      border: "none", backgroundColor: "transparent", color: "#fca5a5", cursor: "pointer",
                      fontFamily: "'Roboto Mono', Consolas, monospace", fontSize: "13px", letterSpacing: "0.3px",
                    }}
                  >
                    🚪&nbsp;&nbsp;Esci
                  </button>
                </div>
              </>
            )}
          </div>
          <span
            title={COMMIT ? `Build ${DATA_BUILD} · commit ${COMMIT}` : `Build ${DATA_BUILD}`}
            style={{ fontSize: "11px", color: "#64748b", letterSpacing: "0.3px", whiteSpace: "nowrap" }}
          >
            v{VERSIONE} build {DATA_BUILD.slice(2).replace(/-/g, "")}
          </span>
          {sessione && (
            <>
              <span style={{ color: "#475569" }}>·</span>
              <span style={{ color: "#94a3b8" }}>{sessione}</span>
            </>
          )}
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: "18px" }}>
          {username && (
            <span style={{ color: "#94a3b8" }}>
              👤&nbsp;<span style={{ color: "#e2e8f0", fontWeight: 700 }}>{username}</span>
              {azienda && <span style={{ color: "#64748b" }}>&nbsp;· {azienda}</span>}
            </span>
          )}
        </div>
      </div>

      <div style={{ padding: "22px 24px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", flexWrap: "wrap", gap: "14px" }}>
          <div style={{ display: "flex", alignItems: "center", gap: "14px" }}>
            <div style={{ display: "flex", flexDirection: "column", alignItems: "center" }}>
              <img src="/logo.png" alt="Logo" style={{ height: "44px", width: "auto" }} />
              <span style={{ fontFamily: "'Montserrat', 'Segoe UI', sans-serif", fontSize: "21px", lineHeight: 1, marginTop: "1px", color: "#f1f5f9" }}>
                <span style={{ fontWeight: 800 }}>w</span>
                <span style={{ fontWeight: 300 }}>Pulse</span>
              </span>
            </div>
            <div>
              <h1
                style={{
                  margin: 0,
                  fontSize: "26px",
                  fontWeight: 700,
                  fontFamily: "'Roboto Mono', Consolas, monospace",
                  letterSpacing: "1px",
                  color: "#f1f5f9",
                }}
              >
                Connector
              </h1>
            </div>
          </div>

          <div style={{ display: "flex", gap: "2px", backgroundColor: "#0f172a", borderRadius: "6px", padding: "4px", border: "1px solid #475569" }}>
            {vociNav.map((voce) => {
              const attiva = location.pathname === voce.path;
              return (
                <NavLink
                  key={voce.path}
                  to={voce.path}
                  className="cf-btn"
                  style={{
                    padding: "11px 20px",
                    borderRadius: "4px",
                    border: "none",
                    cursor: "pointer",
                    fontWeight: 700,
                    fontFamily: "'Roboto Mono', Consolas, monospace",
                    fontSize: "14px",
                    letterSpacing: "0.5px",
                    textDecoration: "none",
                    backgroundColor: attiva ? "#22d3ee" : "transparent",
                    color: attiva ? "#0b1220" : "#cbd5e1",
                    transition: "0.2s",
                  }}
                >
                  {voce.etichetta}
                </NavLink>
              );
            })}
          </div>
        </div>
      </div>
    </header>
  );
}

function Shell({ temaScuro, setTemaScuro }) {
  const location = useLocation();
  const navigate = useNavigate();
  const { moduloAttivo, setModuloAttivo, tracciato, setTracciato, resetFlusso, moduliAttivi } = useApp();
  const [mostraImpostazioni, setMostraImpostazioni] = useState(false);

  // Home/Mapping/Revisione sono gli stessi componenti per ogni modulo di
  // import (Distinta Base, DDT, ...): quale tracciato caricare dipende dal
  // percorso corrente. "/" e "/ordini" non appartengono a nessun modulo con
  // tracciato, quindi non tentano nessun caricamento.
  const inModuloTracciato = location.pathname.startsWith(MODULI.diba.percorsoBase) || location.pathname.startsWith(MODULI.ddt.percorsoBase);
  const modulo = inModuloTracciato ? moduloDaPercorso(location.pathname) : null;

  useEffect(() => {
    if (!modulo || modulo === moduloAttivo) return;
    // Cambio di modulo (es. da Distinta Base a DDT): il tracciato precedente
    // e tutto il flusso in corso (file caricato, mapping, template attivo)
    // non hanno senso nel nuovo modulo, altrimenti resterebbero "a cavallo"
    // tra i due tracciati.
    setModuloAttivo(modulo);
    setTracciato(null);
    resetFlusso();
  }, [modulo, moduloAttivo, setModuloAttivo, setTracciato, resetFlusso]);

  useEffect(() => {
    if (!modulo || tracciato) return;
    getTracciato(modulo).then(setTracciato).catch((err) => console.error(err));
  }, [modulo, tracciato, setTracciato]);

  // Se un modulo viene disattivato dalle Impostazioni mentre qualcuno ha già
  // un link/preferito verso una sua pagina (o lo digita a mano), non deve
  // restare raggiungibile: si torna alla scelta iniziale. "moduliAttivi ===
  // null" (risposta non ancora arrivata) non blocca nulla, per non bloccare
  // per un istante un link diretto valido appena dopo il login.
  useEffect(() => {
    if (!moduliAttivi) return;
    const percorso = location.pathname;
    let chiave = null;
    if (percorso.startsWith("/ordini")) chiave = "ordini";
    else if (percorso.startsWith(MODULI.ddt.percorsoBase)) chiave = "ddt";
    else if (percorso.startsWith(MODULI.diba.percorsoBase)) chiave = "diba";
    if (chiave && moduliAttivi[chiave] === false) navigate("/");
  }, [location.pathname, moduliAttivi, navigate]);

  return (
    <div
      className="app-shell"
      data-theme={temaScuro ? "dark" : "light"}
      style={{ fontFamily: "Inter, sans-serif", padding: "30px", color: "var(--cf-text-primary)", minHeight: "100vh", ...stileSfondoApp(temaScuro) }}
    >
      <style>{CF_STYLE}</style>
      <Intestazione temaScuro={temaScuro} setTemaScuro={setTemaScuro} onApriImpostazioni={() => setMostraImpostazioni(true)} />
      <main>
        <Routes>
          <Route path="/" element={<Scelta />} />
          <Route path="/distinta-base" element={<Home />} />
          <Route path="/distinta-base/mapping" element={<Mapping />} />
          <Route path="/distinta-base/revisione" element={<Revisione />} />
          <Route path="/distinta-base/calibrazione-pdf" element={<CalibrazionePdf />} />
          <Route path="/ddt" element={<Home />} />
          <Route path="/ddt/mapping" element={<Mapping />} />
          <Route path="/ddt/revisione" element={<Revisione />} />
          <Route path="/ddt/calibrazione-pdf" element={<CalibrazionePdf />} />
          <Route path="/ordini" element={<OrdiniMapping />} />
          <Route path="/ordini/mapping" element={<OrdiniMapping />} />
          <Route path="/ordini/calibrazione-pdf" element={<CalibrazionePdf />} />
          <Route path="/ordini/conversione-codici" element={<ConversioneCodici />} />
        </Routes>
      </main>
      {mostraImpostazioni && <PannelloImpostazioni onClose={() => setMostraImpostazioni(false)} />}
    </div>
  );
}

function Contenuto({ temaScuro, setTemaScuro }) {
  const { autenticato, entra } = useAuth();

  if (!autenticato) {
    return <SchermataLogin onLogin={entra} temaScuro={temaScuro} onToggleTema={() => setTemaScuro((v) => !v)} />;
  }

  return (
    <AppProvider>
      <HashRouter>
        <Shell temaScuro={temaScuro} setTemaScuro={setTemaScuro} />
      </HashRouter>
    </AppProvider>
  );
}

export default function App() {
  const [temaScuro, setTemaScuro] = useState(() => {
    try { return localStorage.getItem("cf-tema") === "scuro"; } catch (e) { return false; }
  });

  useEffect(() => {
    try { localStorage.setItem("cf-tema", temaScuro ? "scuro" : "chiaro"); } catch (e) { /* localStorage non disponibile */ }
    document.documentElement.setAttribute("data-theme", temaScuro ? "dark" : "light");
  }, [temaScuro]);

  return (
    <AuthProvider>
      <Contenuto temaScuro={temaScuro} setTemaScuro={setTemaScuro} />
    </AuthProvider>
  );
}
